import React, { useEffect, useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Grid, LinearProgress, TextField, Typography } from "@mui/material";
import { useManagedUnitController } from "./context/managed-unit-controller";
import { useManagedUnitState } from "./context/managed-unit-context";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ClearIcon from '@mui/icons-material/Clear';
import PageHeader from "../components/shared/page-header";
import { IManagementUnitResponse } from "../../model/base/response/management-unit-response";
import FilterPanel, { ResultsPanel } from '../components/shared/filter-panel';
import EmptyState from '../components/shared/empty-state';
import ListSkeleton from '../components/shared/list-skeleton';

export function ManagedUnitComponent() {
    const [initialLoading, setInitialLoading] = useState(true);
    const [loadMoreData, setLoadMoreData] = useState(false);
    const { loadData } = useManagedUnitController();
    const { managedUnit } = useManagedUnitState();
    const [expanded, setExpanded] = React.useState<string | false>(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [filteredUnits, setFilteredUnits] = useState<IManagementUnitResponse[]>([]);

    const handleChange =
        (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
            setExpanded(isExpanded ? panel : false);
        };

    useEffect(() => {
        const searchData = async () => {
            setLoadMoreData(true);
            await loadData();
            setLoadMoreData(false);
            setInitialLoading(false);
        };
        searchData();
    }, []);

    useEffect(() => {
        if (!managedUnit || !Array.isArray(managedUnit)) {
            setFilteredUnits([]);
            return;
        }

        if (searchTerm.trim() === "") {
            setFilteredUnits(managedUnit);
        } else {
            const filtered = managedUnit.filter(unit =>
                unit.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                unit.operation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                unit.othersInformation?.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredUnits(filtered);
        }
    }, [searchTerm, managedUnit]);

    const handleClearFilters = () => {
        setSearchTerm("");
    };

    return (
        <Box>
            <PageHeader
                title="Estrutura Administrativa"
                subtitle="Conheça a estrutura e órgãos da administração municipal. Aqui você encontra informações sobre cada unidade gestora, incluindo endereços, horários de atendimento e contatos."
                breadcrumbs={[{ label: 'Estrutura Administrativa' }]}
            />

            {initialLoading ? (
                <ListSkeleton />
            ) : (
                <React.Fragment>
                    <FilterPanel>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={10} md={10}>
                                <TextField
                                    fullWidth
                                    label="Buscar"
                                    placeholder="Digite para buscar por nome ou informações"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={2} md={2}>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<ClearIcon />}
                                    onClick={handleClearFilters}
                                    fullWidth
                                    sx={{ height: '56px' }}
                                >
                                    Limpar Filtros
                                </Button>
                            </Grid>
                        </Grid>
                    </FilterPanel>

                    <ResultsPanel>
                        {loadMoreData ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                                <LinearProgress sx={{ width: '50%' }} />
                            </Box>
                        ) : filteredUnits.length === 0 ? (
                            <EmptyState title="Nenhuma unidade administrativa encontrada" filtered={searchTerm.trim() !== ''} onClearFilters={handleClearFilters} />
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {filteredUnits.map((element, index) => (
                                    <Accordion
                                        key={index}
                                        expanded={expanded === `panel-${element.id}`}
                                        onChange={handleChange(`panel-${element.id}`)}
                                    >
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Typography fontWeight={600}>
                                                {element?.name}
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                {element?.operation && (
                                                    <Box>
                                                        <Typography variant="body2" fontWeight={600} color="brand.text" sx={{ mb: 0.5 }}>
                                                            Endereço e Horário de Funcionamento
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                                                            {element.operation}
                                                        </Typography>
                                                    </Box>
                                                )}
                                                {element?.othersInformation && (
                                                    <Box>
                                                        <Typography variant="body2" fontWeight={600} color="brand.text" sx={{ mb: 0.5 }}>
                                                            Outras Informações
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                                                            {element.othersInformation}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        </AccordionDetails>
                                    </Accordion>
                                ))}
                            </Box>
                        )}
                    </ResultsPanel>
                </React.Fragment>
            )}
        </Box>
    );
}
